/**
 * CR318 + CR322: Health check functions
 */

import { supabase } from "@/integrations/supabase/client";
import type {
  SnapshotCheck,
  EdgeCheck,
  AuthCheck,
  DbCheck,
  EdgeProbe,
  SupabaseConfigSnapshot,
  FetchErrorDetail,
} from "./types";

// ================================================================
// HELPERS
// ================================================================

/** CR322: Wrap an unknown error into a structured FetchErrorDetail */
function wrapFetchError(err: unknown): FetchErrorDetail {
  if (err instanceof Error) {
    return {
      name: err.name ?? null,
      message: err.message ?? null,
      stack: typeof err.stack === "string" ? err.stack.slice(0, 500) : null,
      cause: typeof (err as any).cause === "string" ? (err as any).cause : null,
    };
  }
  return {
    name: null,
    message: String(err),
    stack: null,
    cause: null,
  };
}

/** CR322: Derive the full edge function URL from the Supabase client config */
function deriveEdgeUrl(functionName: string): string {
  const url = import.meta.env.VITE_SUPABASE_URL;
  return url ? `${url}/functions/v1/${functionName}` : `(unknown)/${functionName}`;
}

// ================================================================
// CHECK: SNAPSHOT
// ================================================================

export async function checkSnapshot(): Promise<SnapshotCheck> {
  try {
    const { data, error } = await supabase.functions.invoke("site-state", {
      method: "GET",
    });

    if (error) {
      return { status: "MISSING", snapshotId: null, publishedAt: null, dealCount: null };
    }

    if (!data?.active_snapshot_id) {
      return { status: "MISSING", snapshotId: null, publishedAt: null, dealCount: null };
    }

    const publishedAt = data.published_at ? new Date(data.published_at) : null;
    const isStale = publishedAt && (Date.now() - publishedAt.getTime()) > 7 * 24 * 60 * 60 * 1000;

    return {
      status: isStale ? "STALE" : "OK",
      snapshotId: data.active_snapshot_id,
      publishedAt: data.published_at,
      dealCount: data.published_deal_count ?? null,
    };
  } catch (err) {
    console.error("[ProductionHealth] Snapshot check error:", err);
    return { status: "MISSING", snapshotId: null, publishedAt: null, dealCount: null };
  }
}

// ================================================================
// CHECK: EDGE (CR322 expanded)
// ================================================================

const EDGE_FUNCTION_NAME = "public-site-config";

export async function checkEdge(): Promise<EdgeCheck> {
  const startTime = Date.now();
  const resolvedFunctionName = EDGE_FUNCTION_NAME;
  const resolvedEdgeUrl = deriveEdgeUrl(EDGE_FUNCTION_NAME);
  let hasAuth = false;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    hasAuth = !!session?.access_token;

    const response = await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      method: "GET",
    });

    const latencyMs = Date.now() - startTime;

    if (response.error) {
      const errorMessage = response.error.message || String(response.error);
      const isNetworkError = errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError");

      return {
        status: "ERROR",
        httpStatus: null,
        resolvedFunctionName,
        resolvedEdgeUrl,
        requestMethod: "GET",
        requestHeadersPresent: { authorization: hasAuth, contentType: false },
        requestBodyBytes: null,
        fetchError: {
          name: isNetworkError ? "TypeError" : "FunctionsError",
          message: errorMessage,
          stack: null,
          cause: isNetworkError ? "Failed to fetch - edge function may not be deployed" : null,
        },
        latencyMs,
        payloadSnippet: null,
        preflightAttempted: true,
        preflightStatus: null,
        invokeAttempted: true,
      };
    }

    return {
      status: "OK",
      httpStatus: 200,
      resolvedFunctionName,
      resolvedEdgeUrl,
      requestMethod: "GET",
      requestHeadersPresent: { authorization: hasAuth, contentType: false },
      requestBodyBytes: null,
      fetchError: null,
      latencyMs,
      payloadSnippet: response.data ? "Config returned OK" : null,
      preflightAttempted: true,
      preflightStatus: 200,
      invokeAttempted: true,
    };
  } catch (err) {
    const latencyMs = Date.now() - startTime;
    const isTimeout = latencyMs > 10000;
    console.error("[ProductionHealth] Edge check error:", err);
    return {
      status: isTimeout ? "TIMEOUT" : "ERROR",
      httpStatus: null,
      resolvedFunctionName,
      resolvedEdgeUrl,
      requestMethod: "GET",
      requestHeadersPresent: { authorization: hasAuth, contentType: false },
      requestBodyBytes: null,
      fetchError: wrapFetchError(err),
      latencyMs,
      payloadSnippet: null,
      preflightAttempted: true,
      preflightStatus: null,
      invokeAttempted: true,
    };
  }
}

// ================================================================
// CHECK: AUTH
// ================================================================

export async function checkAuth(): Promise<AuthCheck> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      return { status: "NO_SESSION", hasSession: false, hasToken: false, errorCode: null };
    }

    if (!session) {
      return { status: "NO_SESSION", hasSession: false, hasToken: false, errorCode: null };
    }

    const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
    if (expiresAt && expiresAt < new Date()) {
      return { status: "EXPIRED", hasSession: true, hasToken: true, errorCode: null };
    }

    return {
      status: "OK",
      hasSession: true,
      hasToken: !!session.access_token,
      errorCode: null,
    };
  } catch (err) {
    console.error("[ProductionHealth] Auth check error:", err);
    return { status: "NO_SESSION", hasSession: false, hasToken: false, errorCode: null };
  }
}

// ================================================================
// CHECK: DB
// ================================================================

export async function checkDb(): Promise<DbCheck> {
  const startTime = Date.now();
  try {
    const { data, error } = await supabase
      .from("deal_type_registry")
      .select("deal_type_key")
      .limit(1);

    const latencyMs = Date.now() - startTime;

    if (error) {
      return { status: "ERROR", message: error.message, latencyMs };
    }

    return { status: "OK", message: null, latencyMs };
  } catch (err) {
    const latencyMs = Date.now() - startTime;
    console.error("[ProductionHealth] DB check error:", err);
    return { status: "ERROR", message: String(err), latencyMs };
  }
}

// ================================================================
// CR322: EDGE PROBE
// ================================================================

const PROBE_FUNCTION_NAME = "public-site-config";

export async function runEdgeProbe(): Promise<EdgeProbe> {
  const startTime = Date.now();
  try {
    const { data, error } = await supabase.functions.invoke(PROBE_FUNCTION_NAME, {
      method: "GET",
    });

    const latencyMs = Date.now() - startTime;

    if (error) {
      return {
        functionName: PROBE_FUNCTION_NAME,
        attempted: true,
        status: "ERROR",
        httpStatus: null,
        latencyMs,
        error: error.message || String(error),
      };
    }

    return {
      functionName: PROBE_FUNCTION_NAME,
      attempted: true,
      status: "OK",
      httpStatus: 200,
      latencyMs,
      error: null,
    };
  } catch (err) {
    const latencyMs = Date.now() - startTime;
    console.error("[ProductionHealth] Edge probe error:", err);
    return {
      functionName: PROBE_FUNCTION_NAME,
      attempted: true,
      status: "ERROR",
      httpStatus: null,
      latencyMs,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

// ================================================================
// CR322: SUPABASE CONFIG SNAPSHOT
// ================================================================

export function captureSupabaseConfig(): SupabaseConfigSnapshot {
  const url = import.meta.env.VITE_SUPABASE_URL ?? null;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "";

  let projectRef: string | null = null;
  if (url) {
    const match = url.match(/https:\/\/([a-z0-9]+)\.supabase\.co/);
    if (match) {
      projectRef = match[1];
    }
  }

  return {
    url,
    anonKeyPresent: anonKey.length > 0,
    projectRefDerived: projectRef,
    clientInitSource: url ? "env" : null,
  };
}

// ================================================================
// CR322: DEBUG SUMMARY
// ================================================================

export function buildEdgeDebugSummary(
  edge: EdgeCheck | null,
  edgeProbe: EdgeProbe | null,
  supabaseConfig: SupabaseConfigSnapshot | null
): string | null {
  if (!edge && !edgeProbe) return null;

  const parts: string[] = [];

  if (edgeProbe?.attempted) {
    const probeUrl = supabaseConfig?.url
      ? `${supabaseConfig.url}/functions/v1/${edgeProbe.functionName}`
      : edgeProbe.functionName;
    const authFlag = edge?.requestHeadersPresent?.authorization ? "true" : "false";
    const result = edgeProbe.status === "OK"
      ? `OK (${edgeProbe.latencyMs}ms)`
      : `ERROR (${edgeProbe.error || "unknown"})`;
    parts.push(`edgeProbe(${edgeProbe.functionName}) via ${probeUrl} auth=${authFlag} → ${result}`);
  }

  if (edge) {
    const edgeResult = edge.status === "OK"
      ? `OK HTTP ${edge.httpStatus} (${edge.latencyMs}ms)`
      : `${edge.status} (${edge.fetchError?.message || "unknown"})`;
    parts.push(`edgeCheck(${edge.resolvedFunctionName || "?"}) → ${edgeResult}`);
  }

  return parts.join(" | ") || null;
}
